/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CollapsableSearchBoxProps } from 'src/core/components/CollapsableSearchBox';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';

export type PhraseListTableCommandBarProps = TableCommandBarProps<PhraseList> & {
    /**
     * Callback for creating a phrase list.
     */
    onCreatePhraseList: () => boolean | void;

    /**
     * Callback for deleting a phrase list.
     */
    onDeletePhraseLists: () => boolean | void;

    /**
     * Callback for make global a phrase list.
     */
    onMakePhraseListGlobal: () => boolean | void;

    /**
     * User role to extract actions available for user.
     */
    userRole: string;

    /**
     * User role to show in luis tool tip
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    addPhraseListButton: {
        id: 'luis.features.phraseListTableCommandBar.addPhraseListButton',
        defaultMessage: 'Create'
    },
    deletePhraseListButton: {
        id: 'luis.features.phraseListTableCommandBar.deletePhraseListButton',
        defaultMessage: 'Delete'
    },
    makeGlobalPhraseListButton: {
        id: 'luis.features.phraseListTableCommandBar.makeGlobalPhraseListButton',
        defaultMessage: 'Make global'
    },
    search: {
        id: 'luis.features.phraseListTableCommandBar.search',
        defaultMessage: 'Search for feature(s)'
    }
});

/**
 * Represents a command bar for the phrase list table.
 *
 * @param props The properties for the phrase list table command bar.
 */
export const PhraseListTableCommandBar = (props: PhraseListTableCommandBarProps) => {
    const { selectedItems = [], query, onQueryChange, userRole, displayedUserRole } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'add',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="addButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            iconProps={{ iconName: 'Add' }}
                            data-automation-id="addButton"
                            onClick={props.onCreatePhraseList}
                            disabled={!canWrite || item.disabled}
                            name={localize(messages.addPhraseListButton)}
                            text={localize(messages.addPhraseListButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'makeGlobal',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="makeGlobalButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            iconProps={{ iconName: 'Globe' }}
                            data-automation-id="makeGlobalButton"
                            disabled={!canWrite || item.disabled}
                            onClick={props.onMakePhraseListGlobal}
                            name={localize(messages.makeGlobalPhraseListButton)}
                            text={localize(messages.makeGlobalPhraseListButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            data-automation-id="deleteButton"
                            iconProps={{ iconName: 'Delete' }}
                            onClick={props.onDeletePhraseLists}
                            disabled={!canDeleteData || item.disabled}
                            name={localize(messages.deletePhraseListButton)}
                            text={localize(messages.deletePhraseListButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    const items: ICommandBarItemProps[] = getCommandBarItems(commands, selectedItems);

    const handleSearchBoxChange: CollapsableSearchBoxProps['onChange'] = (_, newValue?: string) => onQueryChange(newValue);

    const farCommands: ICommandBarItemProps[] = [
        {
            key: 'search',
            iconProps: { iconName: 'Search' },
            iconOnly: true,
            onRender: () => (
                <LuisDebouncedSearchBox
                    value={query}
                    onChange={handleSearchBoxChange}
                    ariaLabel={localize(messages.search)}
                    placeholder={localize(messages.search)}
                />
            )
        }
    ];

    return <LuisCommandBar items={items} farItems={farCommands} />;
};

/**
 * Gets the commands to display in the command bar based on the
 * selected items.
 *
 * @param commands All the commands supported.
 * @param selectedItems Total number of selected items.
 * @returns An array of filtered commands.
 */
const getCommandBarItems = (commands: ICommandBarItemProps[], selectedItems: PhraseList[]): ICommandBarItemProps[] => {
    const updatedCommands: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['add'],
        ['delete', 'makeGlobal'],
        ['delete', 'makeGlobal'],
        selectedItems.length
    );

    /**
     *  Hide make global button if the selected phrase list is already global.
     */
    if (selectedItems.length === 1 && selectedItems[0].isGlobal) {
        updatedCommands.find(c => c.key === 'makeGlobal').disabled = true;
    }

    return updatedCommands;
};
